import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth tokens
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Product Service
 * GET /api/products
 * GET /api/products/:id
 * POST /api/products
 * PUT /api/products/:id
 * DELETE /api/products/:id
 */
export const productService = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    return apiClient.get(`/products?${params}`);
  },
  
  getById: async (id) => {
    return apiClient.get(`/products/${id}`);
  },
  
  create: async (data) => {
    return apiClient.post('/products', data);
  },
  
  update: async (id, data) => {
    return apiClient.put(`/products/${id}`, data);
  },
  
  delete: async (id) => {
    return apiClient.delete(`/products/${id}`);
  },
};

/**
 * Cart Service
 * POST /api/cart/add
 * PUT /api/cart/update
 * DELETE /api/cart/remove
 * POST /api/cart/checkout
 */
export const cartService = {
  add: async (productId, quantity, installOption) => {
    return apiClient.post('/cart/add', { productId, quantity, installOption });
  },
  
  update: async (cartItemId, quantity) => {
    return apiClient.put('/cart/update', { cartItemId, quantity });
  },
  
  remove: async (cartItemId) => {
    return apiClient.delete('/cart/remove', { data: { cartItemId } });
  },
  
  checkout: async (cartData) => {
    return apiClient.post('/cart/checkout', cartData);
  },
};

/**
 * Booking Service
 * GET /api/bookings
 * POST /api/bookings
 * PUT /api/bookings/:id
 * DELETE /api/bookings/:id
 */
export const bookingService = {
  getAll: async () => {
    return apiClient.get('/bookings');
  },
  
  create: async (data) => {
    return apiClient.post('/bookings', data);
  },
  
  update: async (id, data) => {
    return apiClient.put(`/bookings/${id}`, data);
  },
  
  delete: async (id) => {
    return apiClient.delete(`/bookings/${id}`);
  },
};

/**
 * Service Service
 * GET /api/services
 * GET /api/services/:id
 */
export const serviceService = {
  getAll: async () => {
    return apiClient.get('/services');
  },
  
  getById: async (id) => {
    return apiClient.get(`/services/${id}`);
  },
};

/**
 * Admin Service
 * GET /api/admin/dashboard
 * GET /api/admin/products
 * GET /api/admin/orders
 * GET /api/admin/bookings
 * GET /api/admin/inventory
 * GET /api/admin/b2b
 */
export const adminService = {
  getDashboard: async () => {
    return apiClient.get('/admin/dashboard');
  },
  
  getProducts: async () => {
    return apiClient.get('/admin/products');
  },
  
  getOrders: async () => {
    return apiClient.get('/admin/orders');
  },
  
  getBookings: async () => {
    return apiClient.get('/admin/bookings');
  },
  
  getInventory: async () => {
    return apiClient.get('/admin/inventory');
  },
  
  updateInventory: async (data) => {
    return apiClient.post('/admin/inventory', data);
  },
  
  getB2BAccounts: async () => {
    return apiClient.get('/admin/b2b');
  },
};

/**
 * Auth Service
 * POST /api/auth/login
 * POST /api/auth/register
 * POST /api/auth/logout
 */
export const authService = {
  login: async (credentials) => {
    return apiClient.post('/auth/login', credentials);
  },
  
  register: async (userData) => {
    return apiClient.post('/auth/register', userData);
  },
  
  logout: async () => {
    return apiClient.post('/auth/logout');
  },
};

export default apiClient;

